import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  FaSun,
  FaBolt,
  FaWater,
  FaLeaf,
  FaTemperatureHigh,
  FaSolarPanel,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const featureIconMapping = {
  FaBolt: FaBolt,
  FaWater: FaWater,
  FaLeaf: FaLeaf,
};

const rightPanelIconMapping = {
  FaBolt: FaBolt,
  FaTemperatureHigh: FaTemperatureHigh,
};

function PVTExplanation() {
  const { t } = useTranslation();
  const pvtData = t("components.solution.pvtExplanation", {
    returnObjects: true,
  });
  const {
    badge,
    mainHeading,
    highlightText,
    description,
    features,
    rightPanel,
  } = pvtData;

  const featureItems = useMemo(
    () =>
      features.map((feature, index) => {
        const IconComponent = featureIconMapping[feature.icon] || null;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2 }}
            className="flex items-start gap-4"
          >
            <div
              className={`p-3 rounded-xl bg-linear-to-r ${feature.color} text-gray-900`}
            >
              {IconComponent && (
                <IconComponent className="w-6 h-6" aria-hidden="true" />
              )}
            </div>
            <div>
              <h3 className="text-md lg:text-lg font-semibold text-gray-100 mb-1">
                {feature.title}
              </h3>
              <p className="text-md lg:text-lg text-gray-400">
                {feature.description}
              </p>
            </div>
          </motion.div>
        );
      }),
    [features]
  );

  const gridItems = useMemo(
    () =>
      rightPanel.gridItems.map((item, index) => {
        const GridIcon = rightPanelIconMapping[item.icon] || null;
        return (
          <div key={index} className={`p-4 ${item.bgColor} rounded-xl`}>
            <div className="flex items-center gap-2 mb-2">
              {GridIcon && (
                <GridIcon className={item.iconColor} aria-hidden="true" />
              )}
              <span className="text-md font-medium text-gray-200">
                {item.title}
              </span>
            </div>
            <p className="text-sm lg:text-md text-gray-400">
              {item.efficiency}
            </p>
          </div>
        );
      }),
    [rightPanel.gridItems]
  );

  const statsItems = useMemo(
    () =>
      rightPanel.stats.map((stat, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
        >
          <span className={`text-md font-medium ${stat.labelColor}`}>
            {stat.label}
          </span>
          <span
            className={`text-sm lg:text-md text-right font-bold ${stat.statTextColor}`}
          >
            {stat.value}
          </span>
        </div>
      )),
    [rightPanel.stats]
  );

  return (
    <section
      className="overflow-x-hidden py-20 bg-linear-to-b from-gray-900 to-black text-gray-200"
      id="safePad"
      aria-label="PVT System Explanation"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex-1 space-y-8"
          >
            <div className="inline-flex items-center gap-2 bg-blue-900/30 px-4 py-2 rounded-full">
              <FaSun className="text-yellow-500" aria-hidden="true" />
              <span className="text-sm sm:text-base text-blue-200 font-medium">
                {badge}
              </span>
            </div>

            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-100 leading-tight">
              {mainHeading}
              <br />
              <span className="bg-linear-to-r from-blue-500 to-green-500 text-transparent bg-clip-text">
                {highlightText}
              </span>
            </h2>

            <p className="text-md lg:text-lg text-gray-400 leading-relaxed">
              {description}
            </p>

            <div className="grid gap-6">{featureItems}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="w-full md:w-[500px] lg:w-[450px]"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-linear-to-r from-blue-500/30 to-green-500/30 blur-3xl" />
              <div className="relative bg-gray-800 rounded-2xl shadow-xl p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-center gap-4 p-4 bg-blue-700/50 rounded-xl">
                    <FaSolarPanel
                      className="w-8 h-8 text-blue-400"
                      aria-hidden="true"
                    />
                    <div>
                      <h4 className="text-md font-semibold text-gray-100">
                        {rightPanel.header}
                      </h4>
                      <p className="text-sm lg:text-md text-gray-400">
                        {rightPanel.subheader}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">{gridItems}</div>

                  <div className="space-y-3">{statsItems}</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default React.memo(PVTExplanation);
